// -----------------------------------------------------------
// FloraIQ - LADM Overview (Aba 1: Visão Geral)
// -----------------------------------------------------------

import { type FC, useState } from "react";
import { ladmOverviewGroups } from "@/mocks/ladm";

const LadmOverview: FC = () => {
  const [activeDetail, setActiveDetail] = useState<string | null>(null);

  const colorMap: Record<string, { ring: string; progressBg: string; progressFill: string; iconBg: string; iconText: string; hoverBg: string }> = {
    success: { ring: "border-l-green-500", progressBg: "bg-green-100", progressFill: "bg-green-500", iconBg: "bg-green-100", iconText: "text-green-700", hoverBg: "hover:bg-green-50/30" },
    info: { ring: "border-l-blue-500", progressBg: "bg-blue-100", progressFill: "bg-blue-500", iconBg: "bg-blue-100", iconText: "text-blue-700", hoverBg: "hover:bg-blue-50/30" },
    warning: { ring: "border-l-orange-500", progressBg: "bg-orange-100", progressFill: "bg-orange-500", iconBg: "bg-orange-100", iconText: "text-orange-700", hoverBg: "hover:bg-orange-50/30" },
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-foreground-900 font-heading">Grupos Metodológicos</h3>
      <p className="text-xs text-foreground-500 -mt-4">
        Cinco pilares estruturam o cadastramento arbóreo como base territorial inteligente no modelo FloraIQ-LADM.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {ladmOverviewGroups.map((group) => {
          const colors = colorMap[group.color] || colorMap.info;
          const isExpanded = activeDetail === group.id;
          return (
            <div
              key={group.id}
              className={`bg-white rounded-card border border-background-200/70 p-5 border-l-4 ${colors.ring} transition-all duration-200 cursor-pointer ${colors.hoverBg}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${group.icon} ${colors.iconText} text-base`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-900 font-heading">{group.title}</h4>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{group.description}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">Completude</span>
                  <span className="font-semibold text-foreground-900">{group.completeness}%</span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${colors.progressBg}`}>
                  <div
                    className={`h-1.5 rounded-full ${colors.progressFill} transition-all duration-500`}
                    style={{ width: `${group.completeness}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">{group.total} itens esperados</span>
                  {group.pending > 0 ? (
                    <span className="text-orange-600 font-medium">{group.pending} pendências</span>
                  ) : (
                    <span className="text-green-600 font-medium">Completo</span>
                  )}
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDetail(isExpanded ? null : group.id);
                }}
                className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap"
              >
                {isExpanded ? "Recolher" : "Ver detalhes"} <i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-right-line"} ml-1`}></i>
              </button>

              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-background-200/50 slide-up">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded-md bg-background-50/70">
                      <span className="text-foreground-400">Concluídos</span>
                      <p className="font-semibold text-foreground-900">{group.total - group.pending}</p>
                    </div>
                    <div className="p-2 rounded-md bg-background-50/70">
                      <span className="text-foreground-400">Pendentes</span>
                      <p className="font-semibold text-orange-600">{group.pending}</p>
                    </div>
                    <div className="p-2 rounded-md bg-background-50/70">
                      <span className="text-foreground-400">Total esperado</span>
                      <p className="font-semibold text-foreground-900">{group.total}</p>
                    </div>
                    <div className="p-2 rounded-md bg-background-50/70">
                      <span className="text-foreground-400">Índice</span>
                      <p className="font-semibold text-green-700">{group.completeness}%</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LadmOverview;